//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnifiedMetricsTouchpointStatsResponse } from '@bvengineering/metrics-gateway-service-types';
import {
  GetPinterestAccountAudienceSummaryStatsResponse,
  GetPinterestEngagementUserMetricsResponse,
  PinterestAccountAudienceStats,
} from '@bvengineering/pinterest-gateway-service-types';
import { useQuery } from '@tanstack/react-query';

import { getClientCampaignBvIds } from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApi';
import { GetClientSamplingCampaignBvIdsResponse } from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApiModel';
import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import { IContentLibraryItemParams } from '@apis/content-library/contentLibraryApiModel';
import { getCustomGalleries, getGalleries } from '@apis/galleries/galleriesApi';
import { getDailyAudienceMetrics } from '@apis/gateway-instagram/instagramAudienceMetricsApi';
import {
  IInstagramDailyAudienceMetricsRequest,
  IInstagramDailyAudienceMetricsResponse,
} from '@apis/gateway-instagram/instagramAudienceMetricsModel';
import {
  getAudienceStats as getFacebookAudienceStats,
  getEngagementStats as getFacebookEngagementStats,
} from '@apis/gatewayFacebookApi';
import {
  getDefaultInstagramUser,
  getDefaultInstagramUserPostsStats,
} from '@apis/instagram-user/instagramUserApi';
import {
  IDefaultInstagramUserResponse,
  IInstagramDefaultUserPostsStatsRequest,
  IInstagramDefaultUserPostsStatsResponse,
} from '@apis/instagram-user/instagramUserApiModel';
import {
  getBubbleStats,
  getGraphStats,
  getItemStats,
  getMetadata,
  getTouchpointStats,
} from '@apis/metrics/metricsApi';
import {
  IItemProductStatsRequest,
  IStatsRequest,
} from '@apis/metrics/metricsApiModel';
import {
  getAudienceDailyMetrics,
  getAudienceSummaryMetrics,
  getEngagementMetrics,
} from '@apis/pinterest-analytics/pinterestAnalyticsApi';
import { getConnectionStatus } from '@apis/pinterest-oauth/pinterestOAuthApi';
import {
  getAudienceDailyStats as getTiktokAudienceDailyStats,
  getAudienceSummaryStats as getTiktokAudienceSummaryStats,
  getEngagementStats as getTiktokEngagementStats,
} from '@apis/tiktok-analytics/tiktokAnalyticsApi';
import {
  IGetDailyMetricsResult,
  ITikTokAudienceDailyMetrics,
  ITikTokEngagementStats,
} from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import {
  getAudienceDailyMetrics as getTwitterAudienceDailyMetrics,
  getAudienceSummaryMetrics as getTwitterAudienceSummaryMetrics,
} from '@apis/twitter-analytics/twitterAnalyticsApi';
import {
  IGetAudienceDailyStatsResponse,
  IGetAudienceSummaryStatsResponse,
} from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import {
  IPageAudienceMetricsResponse,
  IPageMetricsResponse,
} from '@facebook/models/facebookStateModel';
import DateString from '@models/DateString';
import FacebookPageId from '@models/FacebookPageId';
import TikTokUserId from '@models/TikTokUserId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { GRID_MAX_DISPLAY_ITEMS } from '@reporting/constants/gridWidgetConstants';
import { useCheckPinterestRestrictions } from '@reporting/hooks/useCheckPinterestRestrictions';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export const useGetInsightsBubbleStatsQuery = (req: IStatsRequest) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceBubbleStats,
      req,
    ],
    queryFn: ({ signal }) => {
      return getBubbleStats(req, { signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const getInsightsGraphStatsQueryOptions = (req: IStatsRequest) => {
  return {
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceGraphStats,
      req,
    ],
    queryFn: ({ signal }: { signal?: AbortSignal }) => {
      return getGraphStats(req, { signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  };
};

export const useGetContentLibraryItems = (
  params: IContentLibraryItemParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingContentLibraryItems,
      params,
    ],
    enabled,
    queryFn: ({ signal }) => getContentLibraryItems(params, { signal }),
  });
};

export const useGetCampaignsContentItemsQuery = (
  params: IContentLibraryItemParams,
  enabled = true,
) => {
  const queryParams = {
    ...params,
    limit: params.limit ?? GRID_MAX_DISPLAY_ITEMS,
  };

  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingSocialMedia,
      queryParams,
    ],
    queryFn: async ({ signal }) =>
      getContentLibraryItems(queryParams, { signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled,
  });
};

export const useGetClientCampaignBvIdsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Campaigns,
      QueryKeyConstants.CampaignsSampling,
      QueryKeyConstants.CampaignsSamplingListBvCampaignIds,
    ],
    queryFn: ({ signal }) => getClientCampaignBvIds({ signal }),
    select: (data: GetClientSamplingCampaignBvIdsResponse) =>
      data.bvCampaignIds ?? [],
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGetCustomGalleriesQuery = (enabled = true) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceGalleries,
      QueryKeyConstants.ReportingCommerceGalleriesCustom,
    ],
    queryFn: ({ signal }) => getCustomGalleries({ signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled,
  });
};

export const useGetGalleriesQuery = (enabled = true) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceGalleries,
    ],
    queryFn: ({ signal }) => getGalleries({ signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled,
  });
};

export const useGetItemStatsQuery = (req: IItemProductStatsRequest) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceItemStats,
      req,
    ],
    queryFn: () => {
      return getItemStats(req);
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGetTouchpointStatsQuery = <
  TData = UnifiedMetricsTouchpointStatsResponse[],
>(
  req: IStatsRequest,
  select?: (data: UnifiedMetricsTouchpointStatsResponse[]) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceTouchpointStats,
      req,
    ],
    queryFn: ({ signal }) => {
      return getTouchpointStats(req, { signal });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const usePinterestConnectionStatusQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingPinterest,
      QueryKeyConstants.ReportingPinterestConnectionStatus,
    ],
    queryFn: () => {
      return getConnectionStatus();
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const usePinterestEngagementMetricsQuery = <
  TData = GetPinterestEngagementUserMetricsResponse,
>(
  dateRange: IAliasedDateRange,
  select?: (data: GetPinterestEngagementUserMetricsResponse) => TData,
) => {
  const { canFetchEngagementData } = useCheckPinterestRestrictions(dateRange);
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingPinterest,
      QueryKeyConstants.ReportingPinterestEngagementMetrics,
      dateRange,
    ],
    queryFn: () =>
      getEngagementMetrics({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: canFetchEngagementData,
  });
};

interface UseFacebookEngagementMetricsParams {
  pageId?: FacebookPageId;
  startDate: Date;
  endDate: Date;
}

export const useFacebookEngagementMetricsQuery = <TData = IPageMetricsResponse>(
  req: UseFacebookEngagementMetricsParams,
  select?: (data: IPageMetricsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingFacebook,
      QueryKeyConstants.ReportingFacebookEngagementMetrics,
      req,
    ],
    queryFn: ({ signal }) => {
      if (isUndefined(req.pageId)) {
        throw new Error('Facebook Page ID is required for engagement metrics');
      }
      return getFacebookEngagementStats(
        req.pageId,
        req.startDate,
        req.endDate,
        {
          signal,
        },
      );
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.pageId),
  });
};

export const useFacebookAudienceMetricsQuery = <
  TData = IPageAudienceMetricsResponse,
>(
  req: UseFacebookEngagementMetricsParams,
  select?: (data: IPageAudienceMetricsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingFacebook,
      QueryKeyConstants.ReportingFacebookAudienceMetrics,
      req,
    ],
    queryFn: ({ signal }) => {
      if (isUndefined(req.pageId)) {
        throw new Error('Facebook Page ID is required for audience metrics');
      }
      return getFacebookAudienceStats(req.pageId, req.startDate, req.endDate, {
        signal,
      });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.pageId),
  });
};

export const usePinterestAudienceMetricsQuery = <
  TData = GetPinterestAccountAudienceSummaryStatsResponse,
>(
  pinterestAccountId: string | undefined,
  dateRange: IAliasedDateRange,
  select?: (data: GetPinterestAccountAudienceSummaryStatsResponse) => TData,
) => {
  const { canFetchAudienceData } = useCheckPinterestRestrictions(dateRange);

  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingPinterest,
      QueryKeyConstants.ReportingPinterestAudienceMetrics,
      { pinterestAccountId },
    ],
    queryFn: ({ signal }) => {
      if (!isDefined(pinterestAccountId)) {
        throw new Error(
          'Pinterest Account ID is required for audience metrics',
        );
      }
      return getAudienceSummaryMetrics({ pinterestAccountId }, { signal });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: canFetchAudienceData && isDefined(pinterestAccountId),
  });
};

interface PinterestDailyAudienceMetricsQueryProps {
  dateRange: IAliasedDateRange;
  pinterestAccountId?: string;
}

export const usePinterestDailyAudienceMetricsQuery = <
  TData = PinterestAccountAudienceStats,
>(
  req: PinterestDailyAudienceMetricsQueryProps,
  select?: (data: PinterestAccountAudienceStats) => TData,
) => {
  const { canFetchAudienceData } = useCheckPinterestRestrictions(req.dateRange);

  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingPinterest,
      QueryKeyConstants.ReportingPinterestDailyAudienceMetrics,
      req,
    ],
    queryFn: async () => {
      if (!isDefined(req.pinterestAccountId)) {
        throw new Error(
          'Pinterest Account ID is required for daily audience metrics',
        );
      }

      const response = await getAudienceDailyMetrics({
        pinterestAccountId: req.pinterestAccountId,
        startTime: DateString.toDate(
          req.dateRange.startDate,
          req.dateRange.timeZone,
        ),
        endTime: DateString.toDate(
          req.dateRange.endDate,
          req.dateRange.timeZone,
        ),
      });

      return response.data;
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    // Disable if no account ID or if date range > 30 days
    enabled: isDefined(req.pinterestAccountId) && canFetchAudienceData,
  });
};

export const useTikTokAudienceMetricsQuery = <
  TData = ITikTokAudienceDailyMetrics,
>(
  req: {
    tiktokUserId: TikTokUserId | undefined;
    startTime: Date;
    endTime: Date;
  },
  select?: (data: ITikTokAudienceDailyMetrics) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingTikTok,
      QueryKeyConstants.ReportingTikTokAudienceMetrics,
      req,
    ],
    queryFn: ({ signal }) => {
      if (!isDefined(req.tiktokUserId)) {
        throw new Error('TikTok User ID is required for audience metrics');
      }
      return getTiktokAudienceSummaryStats(
        {
          tiktokUserId: req.tiktokUserId,
          startTime: req.startTime,
          endTime: req.endTime,
        },
        { signal },
      );
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.tiktokUserId),
  });
};

interface TikTokDailyAudienceMetricsQueryProps {
  tiktokUserId?: TikTokUserId;
  startTime: Date;
  endTime: Date;
}

export const useTikTokDailyAudienceMetricsQuery = <
  TData = IGetDailyMetricsResult,
>(
  req: TikTokDailyAudienceMetricsQueryProps,
  select?: (data: IGetDailyMetricsResult) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingTikTok,
      QueryKeyConstants.ReportingTikTokDailyAudienceMetrics,
      req,
    ],
    queryFn: async () => {
      if (!isDefined(req.tiktokUserId)) {
        throw new Error(
          'TikTok User ID is required for daily audience metrics',
        );
      }
      return getTiktokAudienceDailyStats({
        tiktokUserId: req.tiktokUserId,
        startTime: req.startTime,
        endTime: req.endTime,
      });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.tiktokUserId),
  });
};

export const useTwitterAudienceMetricsQuery = <
  TData = IGetAudienceSummaryStatsResponse,
>(
  req: {
    twitterUserId: string | undefined;
    twitterUsername: string | undefined;
    startTime: Date;
    endTime: Date;
  },
  select?: (data: IGetAudienceSummaryStatsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingTwitter,
      QueryKeyConstants.ReportingTwitterAudienceMetrics,
      req,
    ],
    queryFn: ({ signal }) => {
      if (!isDefined(req.twitterUserId) || !isDefined(req.twitterUsername)) {
        throw new Error(
          'Twitter User ID and Username are required for audience metrics',
        );
      }
      return getTwitterAudienceSummaryMetrics(
        {
          twitterUserId: req.twitterUserId,
          twitterUsername: req.twitterUsername,
          startTime: req.startTime,
          endTime: req.endTime,
        },
        { signal },
      );
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.twitterUserId) && isDefined(req.twitterUsername),
  });
};

interface TwitterDailyAudienceMetricsQueryProps {
  twitterUserId?: string;
  startTime: Date;
  endTime: Date;
}

export const useTwitterDailyAudienceMetricsQuery = <
  TData = IGetAudienceDailyStatsResponse,
>(
  req: TwitterDailyAudienceMetricsQueryProps,
  select?: (data: IGetAudienceDailyStatsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingTwitter,
      QueryKeyConstants.ReportingTwitterDailyAudienceMetrics,
      req,
    ],
    queryFn: async () => {
      if (!isDefined(req.twitterUserId)) {
        throw new Error(
          'Twitter User ID is required for daily audience metrics',
        );
      }
      return getTwitterAudienceDailyMetrics({
        twitterUserId: req.twitterUserId,
        startTime: req.startTime,
        endTime: req.endTime,
      });
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(req.twitterUserId),
  });
};

export const useInstagramEngagementMetricsQuery = <
  TData = IInstagramDefaultUserPostsStatsResponse,
>(
  req: IInstagramDefaultUserPostsStatsRequest,
  isInstagramConnected: boolean,
  select?: (data: IInstagramDefaultUserPostsStatsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingInstagram,
      QueryKeyConstants.ReportingInstagramEngagementMetrics,
      req,
    ],
    queryFn: ({ signal }) => getDefaultInstagramUserPostsStats(req, { signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isInstagramConnected,
  });
};

export const useInstagramAudienceMetricsQuery = <
  TData = IInstagramDailyAudienceMetricsResponse,
>(
  req: IInstagramDailyAudienceMetricsRequest,
  isInstagramConnected: boolean,
  select?: (data: IInstagramDailyAudienceMetricsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingInstagram,
      QueryKeyConstants.ReportingInstagramAudienceMetrics,
      req,
    ],
    queryFn: ({ signal }) => getDailyAudienceMetrics(req, { signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isInstagramConnected,
  });
};

export const useInstagramUserQuery = <TData = IDefaultInstagramUserResponse>(
  isInstagramConnected: boolean,
  select?: (data: IDefaultInstagramUserResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingInstagram,
      QueryKeyConstants.ReportingInstagramUser,
    ],
    queryFn: ({ signal }) => getDefaultInstagramUser({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isInstagramConnected,
  });
};

interface UseTikTokSocialEngagementMetricsQueryParams {
  tiktokUsername?: string;
  tiktokDisplayName?: string;
  tiktokUserId?: TikTokUserId;
  startTime: DateString;
  endTime: DateString;
}

export const useTikTokSocialEngagementMetricsQuery = <
  TData = ITikTokEngagementStats,
>(
  req: UseTikTokSocialEngagementMetricsQueryParams,
  isTiktokConnected: boolean,
  select?: (data: ITikTokEngagementStats) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingTikTok,
      QueryKeyConstants.ReportingTikTokEngagementMetrics,
      req,
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(req.tiktokUserId) || !isDefined(req.tiktokUsername)) {
        throw new Error(
          'TikTok User ID and Username are required for engagement metrics',
        );
      }
      return getTiktokEngagementStats(
        {
          tiktokUserId: req.tiktokUserId,
          tiktokUsername: req.tiktokUsername,
          startTime: req.startTime,
          endTime: req.endTime,
          tiktokDisplayName: req.tiktokDisplayName,
        },
        { signal },
      );
    },
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled:
      isTiktokConnected &&
      isDefined(req.tiktokUserId) &&
      isDefined(req.tiktokUsername),
  });
};

export const useGetInsightsMetadataQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Reporting,
      QueryKeyConstants.ReportingCommerce,
      QueryKeyConstants.ReportingCommerceMetadata,
    ],
    queryFn: ({ signal }) => getMetadata({ signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
